import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Shield, AlertTriangle, AlertCircle, CheckCircle2,
  Activity, X, Eye,
  ShieldOff, TrendingDown, Lock,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import api from '../../services/api';

// ─── Types ───────────────────────────────────────────────────────────────────
interface SecurityEvent {
  id: string;
  eventType: string;
  severity: 'INFO' | 'WARNING' | 'CRITICAL';
  description: string;
  resourceType: string | null;
  cvssScore: number | null;
  cveId: string | null;
  isAnomaly: boolean;
  acknowledged: boolean;
  detectedAt: string;
  resolvedAt: string | null;
  daysOpen: number | null;
}

interface HardCondition {
  code: 'HC1' | 'HC2' | 'HC3' | 'HC4';
  name: string;
  desc: string;
  triggered: boolean;
  detail: string | null;
  impact: string;
}

// ─── Mock data ────────────────────────────────────────────────────────────────
const MOCK_EVENTS: SecurityEvent[] = [
  {
    id: 'ev-001', eventType: 'VULN_CVSS_HIGH', severity: 'CRITICAL',
    description: 'CVE-2025-1234 - Remote Code Execution trong Apache Struts 2.5.x',
    resourceType: 'WEB_SERVER', cvssScore: 9.8, cveId: 'CVE-2025-1234',
    isAnomaly: true, acknowledged: false,
    detectedAt: new Date(Date.now() - 25 * 86400_000).toISOString(),
    resolvedAt: null, daysOpen: 25,
  },
  {
    id: 'ev-002', eventType: 'VULN_CVSS_HIGH', severity: 'CRITICAL',
    description: 'CVE-2025-5678 - SQL Injection trong module báo cáo nội bộ',
    resourceType: 'DATABASE', cvssScore: 8.1, cveId: 'CVE-2025-5678',
    isAnomaly: true, acknowledged: false,
    detectedAt: new Date(Date.now() - 35 * 86400_000).toISOString(),
    resolvedAt: null, daysOpen: 35,
  },
  {
    id: 'ev-003', eventType: 'ANOMALY_LOGIN', severity: 'WARNING',
    description: 'Đăng nhập bất thường từ IP 203.113.x.x — ngoài giờ hành chính',
    resourceType: 'IAM', cvssScore: null, cveId: null,
    isAnomaly: true, acknowledged: true,
    detectedAt: new Date(Date.now() - 2 * 86400_000).toISOString(),
    resolvedAt: new Date(Date.now() - 1 * 86400_000).toISOString(), daysOpen: 0,
  },
  {
    id: 'ev-004', eventType: 'DATA_EXFIL_ATTEMPT', severity: 'WARNING',
    description: 'Phát hiện truy vấn lớn bất thường (>500MB) từ user nội bộ',
    resourceType: 'DATABASE', cvssScore: null, cveId: null,
    isAnomaly: true, acknowledged: false,
    detectedAt: new Date(Date.now() - 1 * 86400_000).toISOString(),
    resolvedAt: null, daysOpen: 1,
  },
  {
    id: 'ev-005', eventType: 'PATCH_APPLIED', severity: 'INFO',
    description: 'Đã vá CVE-2024-9999 - Kernel privilege escalation',
    resourceType: 'OS', cvssScore: 7.8, cveId: 'CVE-2024-9999',
    isAnomaly: false, acknowledged: true,
    detectedAt: new Date(Date.now() - 5 * 86400_000).toISOString(),
    resolvedAt: new Date(Date.now() - 4 * 86400_000).toISOString(), daysOpen: 0,
  },
];

const MOCK_HC: HardCondition[] = [
  {
    code: 'HC1',
    name: 'Lỗ hổng CVSS ≥ 7.0 chưa xử lý > 30 ngày',
    desc: 'Nếu có ≥ 1 CVE với CVSS score ≥ 7.0 tồn tại quá 30 ngày mà chưa vá/xử lý → tự động xếp loại C.',
    triggered: true,
    detail: 'CVE-2025-5678 (CVSS 8.1) đã tồn tại 35 ngày · Cần xử lý ngay!',
    impact: 'Tự động xếp loại C (dù điểm tổng ≥ 80)',
  },
  {
    code: 'HC2',
    name: 'Không có kiểm toán trong 12 tháng qua',
    desc: 'Nếu cơ quan chưa có phiên kiểm toán nào được hoàn thành trong 12 tháng gần nhất.',
    triggered: false,
    detail: null,
    impact: 'Tự động xếp loại C',
  },
  {
    code: 'HC3',
    name: 'TC1a = 0 (không có CSDL nào công bố)',
    desc: 'Nếu tỷ lệ CSDL đã công bố = 0% (TC1a nhận điểm 0) → vi phạm nghĩa vụ công bố thông tin.',
    triggered: false,
    detail: null,
    impact: 'Tự động xếp loại C',
  },
  {
    code: 'HC4',
    name: 'Có sự cố lộ lọt dữ liệu được xác nhận',
    desc: 'Nếu trong kỳ kiểm toán có sự cố lộ lọt dữ liệu được C12 xác nhận.',
    triggered: false,
    detail: null,
    impact: 'Tự động xếp loại C',
  },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────
const SEV_COLORS: Record<string, string> = {
  CRITICAL: 'bg-rose-50 text-rose-600 dark:bg-rose-500/10 dark:text-rose-400 border-rose-200 dark:border-rose-500/20',
  WARNING:  'bg-amber-50 text-amber-600 dark:bg-amber-500/10 dark:text-amber-400 border-amber-200 dark:border-amber-500/20',
  INFO:     'bg-blue-50 text-blue-600 dark:bg-blue-500/10 dark:text-blue-400 border-blue-200 dark:border-blue-500/20',
};

const SEV_ICON: Record<string, React.FC<{ size?: number; className?: string }>> = {
  CRITICAL: AlertCircle as React.FC<{ size?: number; className?: string }>,
  WARNING:  AlertTriangle as React.FC<{ size?: number; className?: string }>,
  INFO:     Activity as React.FC<{ size?: number; className?: string }>,
};

// ─── HC Card ─────────────────────────────────────────────────────────────────
function HcCard({ hc }: { hc: HardCondition }) {
  return (
    <div className={`p-4 rounded-2xl border ${
      hc.triggered
        ? 'bg-rose-50 dark:bg-rose-500/10 border-rose-300 dark:border-rose-500/40'
        : 'bg-white dark:bg-white/[0.02] border-slate-200 dark:border-white/5'
    }`}>
      <div className="flex items-start gap-3">
        <div className={`w-8 h-8 rounded-xl flex items-center justify-center shrink-0 ${
          hc.triggered ? 'bg-rose-100 dark:bg-rose-500/20' : 'bg-emerald-100 dark:bg-emerald-500/15'
        }`}>
          {hc.triggered
            ? <ShieldOff size={16} className="text-rose-500" />
            : <CheckCircle2 size={16} className="text-emerald-500" />
          }
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className={`text-[10px] font-black px-2 py-0.5 rounded-full uppercase tracking-wider ${
              hc.triggered ? 'bg-rose-100 dark:bg-rose-500/20 text-rose-600 dark:text-rose-400' : 'bg-slate-100 dark:bg-white/5 text-slate-500'
            }`}>
              {hc.code}
            </span>
            <span className={`text-xs font-black ${hc.triggered ? 'text-rose-600 dark:text-rose-400' : 'text-slate-700 dark:text-slate-300'}`}>
              {hc.name}
            </span>
          </div>
          <p className="text-[11px] text-slate-500 dark:text-slate-400 leading-relaxed">{hc.desc}</p>
          {hc.triggered && hc.detail && (
            <div className="mt-2 p-2.5 rounded-lg bg-rose-100 dark:bg-rose-500/15 border border-rose-200 dark:border-rose-500/20">
              <p className="text-[11px] font-bold text-rose-700 dark:text-rose-400">{hc.detail}</p>
            </div>
          )}
          <p className={`text-[10px] mt-2 font-black ${hc.triggered ? 'text-rose-500' : 'text-slate-400'}`}>
            Hậu quả: {hc.impact}
          </p>
        </div>
        {hc.triggered && (
          <span className="shrink-0 text-[9px] font-black px-2 py-1 rounded-full bg-rose-500 text-white uppercase tracking-wider">
            VI PHẠM
          </span>
        )}
      </div>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function Security() {
  const qc = useQueryClient();
  const [filterSev, setFilterSev] = useState('ALL');
  const [onlyOpen, setOnlyOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'events' | 'hardconditions' | 'cvss'>('hardconditions');
  const [selectedEvent, setSelectedEvent] = useState<SecurityEvent | null>(null);

  const { data: events = MOCK_EVENTS, isLoading } = useQuery<SecurityEvent[]>({
    queryKey: ['security-events-phase2', filterSev, onlyOpen],
    queryFn: () => api.get('/security/events', {
      params: { severity: filterSev !== 'ALL' ? filterSev : undefined, onlyOpen: onlyOpen || undefined },
    }).then(r => r.data ?? MOCK_EVENTS).catch(() => MOCK_EVENTS),
    refetchInterval: 30_000,
  });

  const { data: hcList = MOCK_HC } = useQuery<HardCondition[]>({
    queryKey: ['security-hard-conditions'],
    queryFn: () => api.get('/security/hard-conditions').then(r => r.data ?? MOCK_HC).catch(() => MOCK_HC),
  });

  const ack = useMutation({
    mutationFn: (id: string) => api.post(`/security/events/${id}/acknowledge`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['security-events-phase2'] }); toast.success('Đã xác nhận sự kiện'); },
    onError: () => toast.error('Lỗi xác nhận'),
  });

  const hcTriggered = hcList.filter(h => h.triggered);
  const criticalCount = events.filter(e => e.severity === 'CRITICAL').length;
  const cvssHighOpen  = events.filter(e => (e.cvssScore ?? 0) >= 7 && !e.resolvedAt);
  const cvss30days    = cvssHighOpen.filter(e => (e.daysOpen ?? 0) > 30);

  const filteredEvents = events.filter(e => {
    if (filterSev !== 'ALL' && e.severity !== filterSev) return false;
    if (onlyOpen && e.resolvedAt) return false;
    return true;
  });

  return (
    <div className="space-y-8">
      {/* ── Header ── */}
      <div>
        <h1 className="text-3xl font-black tracking-tight text-slate-900 dark:text-white">
          Giám sát <span className="text-indigo-500">Bảo mật</span>
        </h1>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 font-medium">
          CVSS events · Hard Conditions · HC1 (lỗ hổng &gt;30 ngày → Loại C)
        </p>
      </div>

      {/* ── HC1 Warning Banner ── */}
      {hcTriggered.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-5 rounded-2xl bg-rose-50 dark:bg-rose-500/10 border border-rose-300 dark:border-rose-500/40"
        >
          <div className="flex items-start gap-3">
            <ShieldOff size={22} className="text-rose-500 shrink-0 mt-0.5" />
            <div>
              <p className="font-black text-rose-700 dark:text-rose-400 uppercase tracking-wider text-xs mb-1">
                ⚠ {hcTriggered.length} HARD CONDITION VI PHẠM — TỰ ĐỘNG XẾP LOẠI C
              </p>
              {hcTriggered.map(hc => (
                <p key={hc.code} className="text-sm text-rose-600 dark:text-rose-400 font-bold">
                  [{hc.code}] {hc.detail ?? hc.name}
                </p>
              ))}
            </div>
          </div>
        </motion.div>
      )}

      {/* ── Stat cards ── */}
      <div className="grid grid-cols-4 gap-4">
        {[
          {
            label: 'Hard Conditions',
            value: `${hcTriggered.length} / 4 vi phạm`,
            colorClass: hcTriggered.length > 0 ? 'rose' : 'emerald',
            icon: ShieldOff,
            sub: hcTriggered.length > 0 ? '→ Tự động Loại C' : 'Không vi phạm',
          },
          {
            label: 'Sự kiện CRITICAL',
            value: String(criticalCount),
            colorClass: criticalCount > 0 ? 'rose' : 'emerald',
            icon: AlertCircle,
            sub: `${events.filter(e => !e.acknowledged && e.severity === 'CRITICAL').length} chưa xử lý`,
          },
          {
            label: 'CVSS ≥ 7.0 còn mở',
            value: String(cvssHighOpen.length),
            colorClass: cvssHighOpen.length > 0 ? 'amber' : 'emerald',
            icon: TrendingDown,
            sub: 'Ảnh hưởng điểm TC4c',
          },
          {
            label: 'CVSS > 30 ngày (HC1)',
            value: String(cvss30days.length),
            colorClass: cvss30days.length > 0 ? 'rose' : 'emerald',
            icon: Lock,
            sub: cvss30days.length > 0 ? 'Vi phạm HC1!' : 'Trong hạn',
          },
        ].map((c, i) => (
          <motion.div
            key={c.label}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.07 }}
            className="bg-white dark:bg-white/[0.03] border border-slate-200 dark:border-white/5 rounded-3xl p-5"
          >
            <div className={`w-10 h-10 rounded-2xl flex items-center justify-center mb-3 bg-${c.colorClass}-100 dark:bg-${c.colorClass}-500/15`}>
              <c.icon size={18} className={`text-${c.colorClass}-600 dark:text-${c.colorClass}-400`} />
            </div>
            <p className="text-xl font-black text-slate-900 dark:text-white">{c.value}</p>
            <p className="text-[10px] text-slate-400 font-bold mt-0.5 uppercase tracking-wider">{c.label}</p>
            <p className={`text-[10px] mt-1 font-bold text-${c.colorClass}-600 dark:text-${c.colorClass}-400`}>{c.sub}</p>
          </motion.div>
        ))}
      </div>

      {/* ── Tabs ── */}
      <div className="flex gap-1 bg-slate-100 dark:bg-white/5 p-1 rounded-2xl w-fit">
        {([
          { key: 'hardconditions' as const, label: `Hard Conditions (${hcTriggered.length} vi phạm)` },
          { key: 'cvss' as const, label: `CVSS ≥ 7.0 (${cvssHighOpen.length} mở)` },
          { key: 'events' as const, label: `Tất cả sự kiện (${events.length})` },
        ]).map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-4 py-2 rounded-xl text-xs font-black transition-all ${
              activeTab === tab.key
                ? 'bg-white dark:bg-white/10 text-slate-900 dark:text-white shadow-sm'
                : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── Hard Conditions ── */}
      {activeTab === 'hardconditions' && (
        <div className="space-y-3">
          <p className="text-xs text-slate-500 font-medium">
            4 Hard Conditions theo NĐ 278/2025. Vi phạm bất kỳ điều kiện nào → tự động xếp Loại C bất kể điểm tổng.
          </p>
          {hcList.map(hc => <HcCard key={hc.code} hc={hc} />)}
        </div>
      )}

      {/* ── CVSS ≥ 7.0 ── */}
      {activeTab === 'cvss' && (
        <div className="space-y-3">
          <div className="flex items-start gap-3 p-4 bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/20 rounded-2xl">
            <AlertTriangle size={16} className="text-amber-500 shrink-0 mt-0.5" />
            <p className="text-xs text-amber-700 dark:text-amber-400 font-medium">
              <strong>TC4c scoring:</strong> Số CVE có CVSS ≥ 7.0 chưa xử lý ảnh hưởng trực tiếp đến điểm TC4c.
              Nếu bất kỳ CVE nào tồn tại &gt;30 ngày → kích hoạt <strong>HC1</strong> → tự động Loại C.
            </p>
          </div>

          {cvssHighOpen.length === 0 ? (
            <div className="text-center py-12">
              <CheckCircle2 size={40} className="mx-auto text-emerald-400 mb-3" />
              <p className="text-slate-400 font-medium">Không có lỗ hổng CVSS ≥ 7.0 nào còn mở</p>
            </div>
          ) : (
            cvssHighOpen.map((ev, i) => (
              <motion.div
                key={ev.id}
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.05 }}
                className={`p-4 rounded-2xl border cursor-pointer hover:shadow-md transition-all ${
                  (ev.daysOpen ?? 0) > 30
                    ? 'bg-rose-50 dark:bg-rose-500/5 border-rose-300 dark:border-rose-500/40'
                    : 'bg-white dark:bg-white/[0.02] border-slate-200 dark:border-white/5'
                }`}
                onClick={() => setSelectedEvent(ev)}
              >
                <div className="flex items-start gap-4">
                  <div className="shrink-0">
                    <div className={`px-2.5 py-1.5 rounded-xl text-center ${
                      (ev.cvssScore ?? 0) >= 9 ? 'bg-rose-100 dark:bg-rose-500/20' : 'bg-amber-100 dark:bg-amber-500/20'
                    }`}>
                      <p className={`text-lg font-black tabular-nums ${
                        (ev.cvssScore ?? 0) >= 9 ? 'text-rose-600 dark:text-rose-400' : 'text-amber-600 dark:text-amber-400'
                      }`}>{ev.cvssScore}</p>
                      <p className="text-[8px] font-black text-slate-400 uppercase">CVSS</p>
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      {ev.cveId && (
                        <span className="text-xs font-black font-mono text-slate-700 dark:text-slate-300">{ev.cveId}</span>
                      )}
                      {(ev.daysOpen ?? 0) > 30 && (
                        <span className="text-[9px] font-black px-2 py-0.5 rounded-full bg-rose-500 text-white uppercase">
                          HC1: {ev.daysOpen} ngày
                        </span>
                      )}
                      <span className="text-[9px] text-slate-400">{ev.resourceType}</span>
                    </div>
                    <p className="text-xs text-slate-600 dark:text-slate-400 mt-1">{ev.description}</p>
                    <p className="text-[10px] text-slate-400 mt-1">
                      Phát hiện: {new Date(ev.detectedAt).toLocaleDateString('vi-VN')}
                      {ev.daysOpen !== null && ev.daysOpen > 0 && (
                        <span className="ml-2 font-bold text-rose-500">({ev.daysOpen} ngày chưa xử lý)</span>
                      )}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))
          )}
        </div>
      )}

      {/* ── All Events ── */}
      {activeTab === 'events' && (
        <div className="space-y-4">
          {/* Filters */}
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-1.5">
              {['ALL', 'CRITICAL', 'WARNING', 'INFO'].map(s => (
                <button
                  key={s}
                  onClick={() => setFilterSev(s)}
                  className={`px-3 py-1.5 rounded-xl text-[10px] font-black transition-all ${
                    filterSev === s
                      ? 'bg-indigo-600 text-white'
                      : 'bg-slate-100 dark:bg-white/5 text-slate-500 hover:bg-slate-200 dark:hover:bg-white/10'
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={onlyOpen}
                onChange={e => setOnlyOpen(e.target.checked)}
                className="rounded"
              />
              <span className="text-xs font-bold text-slate-600 dark:text-slate-400">Chỉ chưa xử lý</span>
            </label>
          </div>

          {isLoading ? (
            <div className="text-center py-16 text-slate-400">Đang tải...</div>
          ) : filteredEvents.length === 0 ? (
            <div className="text-center py-12">
              <Shield size={40} className="mx-auto text-slate-300 dark:text-slate-700 mb-3" />
              <p className="text-slate-400 font-medium">Không có sự kiện nào</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredEvents.map((ev, i) => {
                const SevIcon = SEV_ICON[ev.severity] ?? Activity;
                return (
                  <motion.div
                    key={ev.id}
                    initial={{ opacity: 0, x: -8 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.03 }}
                    className={`flex items-start gap-4 p-4 rounded-2xl border cursor-pointer hover:shadow-sm transition-all ${SEV_COLORS[ev.severity]} ${ev.acknowledged ? 'opacity-60' : ''}`}
                    onClick={() => setSelectedEvent(ev)}
                  >
                    <SevIcon size={18} className="shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs font-black">{ev.eventType.replace(/_/g, ' ')}</span>
                        {ev.cvssScore && (
                          <span className="text-[9px] font-black px-1.5 py-0.5 rounded bg-rose-100 dark:bg-rose-500/20 text-rose-600 dark:text-rose-400">
                            CVSS {ev.cvssScore}
                          </span>
                        )}
                        {ev.cveId && <span className="text-[9px] font-mono">{ev.cveId}</span>}
                        {ev.acknowledged && <span className="text-[9px] text-slate-400">✓ ACK</span>}
                      </div>
                      <p className="text-[11px] mt-0.5 opacity-80">{ev.description}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-[10px] opacity-70">{new Date(ev.detectedAt).toLocaleString('vi-VN')}</p>
                      {!ev.acknowledged && !ev.resolvedAt && (
                        <button
                          onClick={e => { e.stopPropagation(); ack.mutate(ev.id); }}
                          className="mt-1.5 text-[9px] font-black px-2 py-0.5 rounded bg-white/30 hover:bg-white/50 transition-all"
                        >
                          ACK
                        </button>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── Event Detail Modal ── */}
      <AnimatePresence>
        {selectedEvent && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white dark:bg-[#161617] border border-slate-200 dark:border-white/10 rounded-3xl w-full max-w-lg shadow-2xl"
            >
              <div className="p-6 border-b border-slate-100 dark:border-white/5 flex items-center justify-between">
                <h3 className="font-black text-slate-900 dark:text-white flex items-center gap-2">
                  <Eye size={16} className="text-indigo-500" /> Chi tiết sự kiện
                </h3>
                <button onClick={() => setSelectedEvent(null)} className="text-slate-400 hover:text-white"><X size={18} /></button>
              </div>
              <div className="p-6 space-y-3">
                {([
                  { l: 'ID', v: selectedEvent.id, mono: true },
                  { l: 'Loại', v: selectedEvent.eventType, mono: false },
                  { l: 'Mức độ', v: selectedEvent.severity, mono: false },
                  { l: 'Mô tả', v: selectedEvent.description, mono: false },
                  ...(selectedEvent.cveId ? [{ l: 'CVE', v: selectedEvent.cveId, mono: true }] : []),
                  ...(selectedEvent.cvssScore != null ? [{ l: 'CVSS Score', v: `${selectedEvent.cvssScore}`, mono: true }] : []),
                  ...(selectedEvent.daysOpen != null ? [{ l: 'Ngày chưa xử lý', v: `${selectedEvent.daysOpen} ngày`, mono: false }] : []),
                  { l: 'Phát hiện lúc', v: new Date(selectedEvent.detectedAt).toLocaleString('vi-VN'), mono: false },
                  ...(selectedEvent.resolvedAt ? [{ l: 'Giải quyết lúc', v: new Date(selectedEvent.resolvedAt).toLocaleString('vi-VN'), mono: false }] : []),
                ]).map((f) => (
                  <div key={f.l} className="flex gap-4">
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider w-32 shrink-0 mt-0.5">{f.l}</span>
                    <span className={`text-xs text-slate-700 dark:text-slate-300 flex-1 ${f.mono ? 'font-mono' : 'font-medium'}`}>{f.v}</span>
                  </div>
                ))}
                {(selectedEvent.daysOpen ?? 0) > 30 && (
                  <div className="mt-2 p-3 rounded-xl bg-rose-50 dark:bg-rose-500/10 border border-rose-200 dark:border-rose-500/20">
                    <p className="text-xs font-black text-rose-600 dark:text-rose-400">
                      ⚠ VI PHẠM HC1: Lỗ hổng CVSS ≥ 7.0 tồn tại quá 30 ngày → Tự động xếp Loại C
                    </p>
                  </div>
                )}
              </div>
              <div className="p-6 pt-0 flex justify-end gap-2">
                {!selectedEvent.acknowledged && !selectedEvent.resolvedAt && (
                  <button
                    onClick={() => { ack.mutate(selectedEvent.id); setSelectedEvent(null); }}
                    className="px-4 py-2 rounded-xl bg-amber-600 text-white text-xs font-black hover:bg-amber-700 transition-all"
                  >
                    Xác nhận (ACK)
                  </button>
                )}
                <button
                  onClick={() => setSelectedEvent(null)}
                  className="px-4 py-2 rounded-xl bg-slate-100 dark:bg-white/5 text-slate-600 dark:text-slate-400 text-xs font-black"
                >
                  Đóng
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
